import styled from "styled-components";
import { Box, Grid, Typography } from "@mui/material";

type CheckProps = {
  $color: "white" | "orange";
};

const colors = {
  primaryBlue: "#146efa",
  darkBlue: "#0b234c",
  orange: "#ff5000",
  orangeAlt: "#fd6520",
  textGray: "#828282",
  textDark: "#323232",
  observation: "#444142",
  white: "#ffffff",
};

export const Card = styled(Box)`
  && {
    position: relative;

    width: 290px;
    padding: 40px 0 15px;
    margin: 10px;
    background: ${colors.white};
    mix-blend-mode: normal;
    box-shadow: 0px -1px 18px 4px rgba(0, 0, 0, 0.21);
    border-radius: 4px;

    @media (max-width: 475px) {
      padding: 27px 0;
      margin: auto;
    }
  }
`;

export const BoxTitle = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 700;
    font-size: 25px;
    line-height: 33px;
    text-align: center;
    color: ${colors.darkBlue};
  }
`;

export const BoxSubtitle = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 16px;
    text-align: center;
    margin: 15px 0 10px;
    height: 30px;
    white-space: pre-wrap;
    color: ${colors.textGray};
  }
`;

export const BoxText = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 21px;
    text-align: center;
    color: ${colors.darkBlue};

    & > span {
      color: ${colors.primaryBlue};
      font-weight: 700;
    }
  }
`;

export const BoxPrice = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 400;
    font-size: 24px;
    line-height: 33px;
    text-align: center;
    color: ${colors.orangeAlt};

    & > span {
      font-size: 42px;
      line-height: 42px;
      font-weight: 800;
    }
  }
`;

export const MiniBoxPrice = styled(BoxPrice)`
  && {
    text-align: center;
  }
`;

export const ValueSave = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
    margin: auto;
    text-align: center;
    color: ${colors.white};
    background-color: ${colors.primaryBlue};
  }
`;

export const HighlightTitle = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 21px;
    text-align: center;
    margin-top: 15px;
    color: ${colors.primaryBlue};
  }
`;

export const HighlightBlock = styled(Box)`
  && {
    display: flex;
    align-items: center;
    padding: 0 28px;
    margin: 15px 0;
    background-color: #146cfa;
    min-height: 80px;
  }
`;

export const HighlightText = styled(Typography)`
  && {
    display: flex;
    align-items: center;
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: ${colors.white};
  }
`;

export const Check = styled.img<CheckProps>`
  height: 14px;
  margin-right: 10px;
  padding-top: 5px;

  filter: ${({ $color }) =>
    $color === "white"
      ? "none"
      : "invert(55%) sepia(93%) saturate(1901%) hue-rotate(0deg) brightness(102%) contrast(101%)"};
`;

export const TeethIcon = styled.img`
  margin-right: 10px;
`;

export const CheckTitle = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 22px;
    padding: 10px 0;
    color: ${colors.textDark};
  }
`;

export const Block = styled(Box)`
  && {
    display: flex;
    align-items: center;
    padding: 5px 28px;
    min-height: 75px;
  }
`;

export const BoxButton = styled(Box)`
  && {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    margin-top: 15px;
  }
`;

export const BoxValue = styled(Box)`
  && {
    margin-top: 10px;

    @media (min-width: 476px) {
      padding: 0 15px;
    }
  }
`;

export const DesktopVersion = styled(Grid)`
  && {
    display: flex;
    max-width: 1920px;
    height: 800px;
    margin: -30px auto 30px;

    @media (max-width: 1400px) {
      margin: -20px auto 30px;
    }

    @media (max-width: 900px) {
      margin-top: -10px;
    }

    @media (max-width: 475px) {
      display: none;
    }
  }
`;

export const MobileVersion = styled(Grid)`
  && {
    display: none;
    margin-bottom: 10px;
    height: 800px;

    @media (max-width: 475px) {
      display: flex;
      height: 800px;
    }

    @media (max-width: 340px) {
      display: flex;
    }
  }
`;

export const TabletVersion = styled(Grid)`
  && {
    display: flex;
    height: 800px;
    margin: -20px auto 0;
  }
`;

export const Observation = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 22px;
    text-align: center;
    letter-spacing: 0.03em;
    color: ${colors.observation};
  }
`;

export const BlueText = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 16px;
    white-space: pre-wrap;
    color: ${colors.primaryBlue};
  }
`;

export const Text = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    text-align: center;
    margin: 15px 0;
    color: ${colors.textGray};
  }
`;

export const PriceText = styled(Typography)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    white-space: pre-wrap;
    color: ${colors.orange};
  }
`;

export const PriceBoxText = styled(BoxText)`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 400;
    font-size: 0.75rem;
    line-height: 18px;
    white-space: pre-wrap;
    color: ${colors.orange};
  }
`;

export const ProductLink = styled.button`
  && {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    margin: 0 auto;

    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 21px;

    text-align: center;
    text-decoration-line: underline;
    text-decoration-color: ${colors.orange};
    color: ${colors.orange};
    cursor: pointer;
  }

  &&:hover {
    color: ${colors.orangeAlt};
    text-decoration-color: ${colors.orangeAlt};
  }

  &&:focus-visible {
    outline: 2px solid ${colors.orange};
    outline-offset: 2px;
    border-radius: 2px;
`;

export const OfferStamp = styled(Box)`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;

  width: 54px;
  height: 54px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: #ffffff;
  border: 2px solid #9c5d28;
  border-radius: 50%;

  transform: rotate(18deg);

  &::before {
    content: "";
    position: absolute;
    inset: 4px;

    border: 1px solid #9c5d28;
    border-radius: 50%;
  }
`;

export const OfferStampText = styled(Typography)`
  && {
    position: relative;
    z-index: 1;

    font-family: Figtree;
    font-style: normal;
    font-weight: 800;
    font-size: 8px;
    line-height: 9px;

    text-align: center;
    white-space: pre-wrap;
    text-transform: uppercase;

    color: #9c5d28;
  }
`;

/**
 * Nova área de preço conforme protótipo.
 */
export const PriceSection = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  width: 100%;
  margin-top: 18px;
  padding-left: 8px;

  min-height: 112px;
`;

export const OldPriceText = styled(Typography)`
  && {
    font-family: Figtree;
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 22px;

    color: #777777;

    span {
      color: #777777;
      text-decoration: line-through;
      text-decoration-thickness: 2px;
    }
  }
`;

export const PriceLabel = styled(Typography)`
  && {
    font-family: Figtree;
    font-style: normal;
    font-weight: 800;
    font-size: 19px;
    line-height: 22px;

    margin-top: 2px;

    color: #c76522;
  }
`;

export const PriceContent = styled(Box)`
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;

  width: 100%;
`;

export const PricePeriod = styled(Typography)`
  && {
    font-family: Figtree;
    font-style: normal;
    font-weight: 800;
    font-size: 20px;
    line-height: 28px;

    margin-left: 4px;
    margin-bottom: 4px;

    color: #c76522;
  }
`;

export const PricePersonText = styled(Typography)`
  && {
    font-family: Figtree;
    font-style: normal;
    font-weight: 600;
    font-size: 17px;
    line-height: 20px;

    width: 100%;
    margin-top: -2px;

    text-align: center;

    color: #323232;
  }
`;